import { ApiFragment } from '../entities/api-fragment.entity';
import { DottedKey } from '../entities/metadata-field.entity';

/** How the engine joins the folders a ZIP upload was unpacked from. */
const PATH_SEPARATOR: string = '/';

/** Leading and trailing separators, which say nothing and would only take up the caption's width. */
const OUTER_SEPARATORS: RegExp = /^\/+|\/+$/g;

/**
 * The separator with a **thin** space either side — U+2009, written as an escape so it is visible in the source
 * instead of being an invisible character somebody deletes by accident. At the caption's 12px the folders
 * otherwise crowd the slash hard enough to be a chore to read, and CSS cannot put air around one character of a
 * text node. A full space would read as a gap between two captions rather than a divider inside one.
 */
const AIRY_SEPARATOR: string = `\u2009${PATH_SEPARATOR}\u2009`;

/**
 * HFBD-7a §12: the folder a stuk came from inside its uploaded ZIP, as it is captioned above the row's content.
 *
 * Reads the **stuk** key alone. That is what keeps dossier rows clean without the caption needing to know a
 * row's level or whether the table is flattened: a dossier's own path is `Dynamic.DossierPath`, and only a
 * document ever carries `Dynamic.DocumentPath`.
 */
export function toPathCaption(record: ApiFragment): string | null {
    const path: unknown = record.getField<unknown>(DottedKey.DocumentPath);

    if (typeof path !== 'string') {
        return null;
    }

    const trimmed: string = normalizePath(path);

    return trimmed.length > 0 ? trimmed : null;
}

/**
 * A path with the noise that says nothing taken off it, so everything reading this field agrees about where
 * its segments begin and end.
 *
 * ⚠ Load-bearing for the submap rule, not only for the caption: with a leading separator the empty first
 * segment would be mistaken for the dossier, and dropping it would eat a real folder.
 */
export function normalizePath(path: string): string {
    return path.trim().replace(OUTER_SEPARATORS, '');
}

/**
 * The caption as it is *typeset*, which is deliberately not what the row carries. The row keeps the real path, so
 * the hover title, a copied selection and — once the treegrid ticket lands — a screen reader all get the path
 * itself rather than one with typography characters wedged into it.
 */
export function toSpacedPathCaption(path: string): string {
    return path.split(PATH_SEPARATOR).join(AIRY_SEPARATOR);
}

/**
 * Reports the one way the caption can fail that no row is able to show: an empty key is **omitted** from a
 * search result rather than returned null, so a record with no path and a projection that never carried the
 * field look identical — the caption is simply absent either way. Only a whole page carrying neither key can
 * tell them apart, which is why this is asked per page rather than per row.
 *
 * What it establishes is that the *family* reaches the frontend at all — hence either key, since a page of
 * dossiers has no `DocumentPath` to carry. It deliberately cannot detect the narrower failure of
 * `DocumentPath` alone being dropped: on a page of dossiers, `DossierPath` answers for both.
 */
export function warnIfPathFamilyMissing(records: ApiFragment[]): void {
    const keys: DottedKey[] = [DottedKey.DocumentPath, DottedKey.DossierPath];

    if (records.length === 0 || records.some(record => keys.some(key => !!record.getField<unknown>(key)))) {
        return;
    }

    console.warn(
        `Pad-annotatie (HFBD-7a): no record on this page carries ${keys.join(' or ')}, so no row can show` +
            ' where it came from. Either these records have no path, or the fields are not being projected' +
            ' into search results.'
    );
}
